import { FC } from 'react';
import { MetricHistoryPoint } from '../useMetricHistory';

interface HighlightDotProps {
  cx?: number;
  cy?: number;
  payload?: MetricHistoryPoint;
  /** Colore base della serie. */
  color: string;
  /** YYYY-MM-DD — giorno da evidenziare (cella cliccata). */
  highlightDate?: string | null;
  /**
   * Se passato, marca come "fuori range" (es. ACWR) i punti — evidenziati o
   * meno — con `outOfRangeColor` invece del colore base.
   */
  isOutOfRange?: (value: number | undefined) => boolean;
  outOfRangeColor?: string;
}

/**
 * Dot custom condiviso dai chart a linea di `MetricHistoryModal`
 * (`MetricLineChart`/`MetricAcwrChart`): cerchio pieno (raggio 2.5),
 * ingrandito (5.5, bordo `--c-card-bg`) sul giorno evidenziato
 * (`highlightDate`). Passato come funzione alla prop `dot` di `<Line />`.
 */
export const HighlightDot: FC<HighlightDotProps> = ({
  cx,
  cy,
  payload,
  color,
  highlightDate,
  isOutOfRange,
  outOfRangeColor = 'var(--c-error)',
}) => {
  if (isOutOfRange && (cx == null || cy == null)) {
    return <circle cx={0} cy={0} r={0} fill="none" />;
  }

  const isHighlighted = highlightDate != null && payload?.date === highlightDate;
  const outOfRange = isOutOfRange ? isOutOfRange(payload?.value) : false;

  if (isHighlighted && cx != null && cy != null) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5.5}
        fill={outOfRange ? outOfRangeColor : color}
        stroke="var(--c-card-bg)"
        strokeWidth={2}
      />
    );
  }

  if (outOfRange) {
    return <circle cx={cx} cy={cy} r={3.5} fill={outOfRangeColor} />;
  }

  return <circle cx={cx ?? 0} cy={cy ?? 0} r={2.5} fill={color} />;
};
